import React, { useContext, useEffect, useState, useCallback } from "react";
import { Grid } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import Button from "@jbhi-fi/lanyard-ui/components/Button/Button";
import TextField from "@jbhi-fi/lanyard-ui/components/TextField/TextField";
import DataGrid from "../Product/Desktop/components/DataGrid";
import { fetchProductHistoryAsync } from "src/infrastructure/http/appApiService";
import { useCountry } from "src/components/Context/CountryContext";
import PageContext from "src/components/Context/PageContext";
import { hasPermission } from "src/infrastructure/auth/PermissionUtils";
import { UserRoles } from "src/infrastructure/auth/UserRoles";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { getConsts } from "../../utils/consts";
import { useTitle } from "src/hooks/useTitle";
import Modal from "@jbhi-fi/lanyard-ui/components/Modal/Modal";
import { makeStyles } from "@mui/styles";
import { ProductHistoryItem, ProductHistoryResponse } from "src/models/product/ProductHistoryResponse";
import { dateTimeType } from "src/utils/dataGridFormat";

type RouteParams = {
  sku?: string;
};

const useStyles = makeStyles((theme) => ({
  modal: {
    maxWidth: "90vw",
  },
}));

const ProductHistory: React.FC = () => {
  const consts = getConsts();
  const history = useHistory();
  const location = useLocation();
  const { sku } = useParams<RouteParams>();
  const classes = useStyles();
  const { country } = useCountry();

  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ProductHistoryItem[] | null>(null);
  const [inputSku, setInputSku] = useState<string>("");
  const [contextData] = useContext(PageContext);
  const { userProfile } = contextData;

  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [productHistoryResponse, setProductHistoryResponse] = useState<ProductHistoryResponse | null>(null);

  const title = sku ? `Product History | ${sku}` : "Product History";
  useTitle(title);

  const runSearch = useCallback(
    async (searchSku: string) => {
      try {
        if (!searchSku) return;
        setErrorMessage(null);
        setIsSearching(true);

        const productHistoryResponse = await fetchProductHistoryAsync(searchSku, country);
        setProductHistoryResponse(productHistoryResponse);

        setIsSearching(false);
      } catch (e) {
        console.error(e);
        setIsSearching(false);
        setProductHistoryResponse(null);
        setErrorMessage("Error while fetching product history.");
      }
    },
    [country]
  );

  useEffect(() => {
    if (sku != null) {
      setInputSku(sku);
      runSearch(sku);
    } else {
      setInputSku("");
      setProductHistoryResponse(null);
      setErrorMessage(null);
    }
  }, [sku, runSearch]);

  if (!hasPermission(UserRoles.ProductHistory, userProfile.roles)) {
    window.location.href = "/";
    return null;
  }

  const getGroupedHistoryRows = () => {
    return productHistoryResponse?.productRecords?.map((record) => ({
      sku: productHistoryResponse.sku,
      ...record,
    }));
  };

  const groupedHistoryColumns: GridColDef[] = [
    {
      field: "sku",
      headerName: "SKU",
      width: 120,
      flex: 1,
    },
    {
      field: "timestamp",
      headerName: "Time of Update",
      width: 120,
      ...dateTimeType,
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Button
          type="primary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click event
            setShowModal(true);
            setSelectedRow(row.productHistoryItems);
          }}
        >
          View Updates
        </Button>
      ),
    },
  ];

  const historyDetailColumns: GridColDef[] = [
    {
      field: "timestamp",
      headerName: "Time",
      width: 180,
      ...dateTimeType,
    },
    {
      field: "changeType",
      headerName: "Event Type",
      width: 120,
      headerAlign: "left",
      align: "left",
      renderCell: ({ value }) => (
        <span
          style={{
            backgroundColor: eventTypeBackgroundColor(value),
            color: eventTypeTextColor(value),
            padding: "4px 8px",
            borderRadius: "4px",
            fontWeight: 500,
            display: "inline-block",
            minWidth: 90,
            textAlign: "center",
          }}
        >
          {value}
        </span>
      ),
    },
    {
      field: "field",
      headerName: "Field",
      width: 150,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "fieldPath",
      headerName: "Field Path",
      minWidth: 120,
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
    {
      field: "oldValue",
      headerName: "Old Value",
      width: 120,
      headerAlign: "left",
      align: "left",
      flex: 1,
      renderCell: ({ value }) => (
        <span style={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: "13px" }}>{value}</span>
      ),
    },
    {
      field: "newValue",
      headerName: "New Value",
      width: 120,
      flex: 1,
      renderCell: ({ value }) => (
        <span style={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: "13px" }}>{value}</span>
      ),
    },
  ];

  const handleSearch = async () => {
    const newPath = `${consts.routerPath.productHistory}/${inputSku}`;

    if (newPath !== location.pathname) {
      history.push(`${consts.routerPath.productHistory}/${inputSku}`);
    }
  };

  const eventTypeBackgroundColor = (eventType: string) => {
    switch (eventType) {
      case "Availability":
        return "#01a6511a";
      case "ProductTags":
        return "#da6a001a";
      case "Display":
        return "#0172bb1a";
      case "Product":
        return "#ffec0f1a";
      default:
        return "#616161";
    }
  };

  const eventTypeTextColor = (eventType: string) => {
    switch (eventType) {
      case "Availability":
        return "#028702";
      case "ProductTags":
        return "#DA6A00";
      case "Display":
        return "#0172BB";
      case "Product":
        return "#9A8F16";
      default:
        return "#ffffff";
    }
  };
  return (
    <>
      {showModal && (
        <Modal
          show={showModal}
          customClass={classes.modal}
          onClose={() => {
            setShowModal(false);
          }}
        >
          <Grid container marginTop={2}>
            <DataGrid
              columns={historyDetailColumns}
              rows={(selectedRow || []).map((item, index) => ({ ...item, id: index }))}
              isLoading={isSearching}
              hideFooter
              autoHeight
              getRowHeight={() => "auto"}
              sx={{
                "& .MuiDataGrid-row": {
                  minHeight: "52px !important",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            ></DataGrid>
          </Grid>
        </Modal>
      )}
      <Grid container paddingBottom={2}>
        <Grid item xs={12} paddingBottom={2}>
          <h2>Product History</h2>
        </Grid>

        <Grid item paddingRight={2} width={320}>
          <TextField
            id="productHistorySearch"
            name="productHistorySearch"
            hasError={errorMessage != null}
            errorMsg={errorMessage}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
            value={inputSku}
            placeholder="Search By SKU"
            onChange={(e) => setInputSku(e.target.value)}
          />
        </Grid>
        <Grid item>
          <Button type={"primary"} onClick={handleSearch} loading={isSearching} disabled={isSearching}>
            SEARCH
          </Button>
        </Grid>
        {productHistoryResponse?.productRecords?.length > 0 && (
          <Grid item sx={{ marginLeft: "auto" }}>
            <Button
              type={"primary"}
              onClick={() => history.push(`${consts.routerPath.product}/${productHistoryResponse.sku}`)}
            >
              View Product Details
            </Button>
          </Grid>
        )}
      </Grid>

      {productHistoryResponse?.productRecords?.length > 0 && (
        <Grid container marginTop={2}>
          <DataGrid
            columns={groupedHistoryColumns}
            rows={getGroupedHistoryRows() || []}
            getRowId={(row) => `${row.sku}-${row.timestamp}`}
            isLoading={isSearching}
            hideFooter
            autoHeight
            disableColumnResize
            getRowHeight={() => "auto"}
            onRowClick={({ row }) => {
              setShowModal(true);
              setSelectedRow(row.productHistoryItems);
            }}
            sx={{
              "& .MuiDataGrid-row": {
                minHeight: "52px !important",
                cursor: "pointer",
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                },
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
              },
            }}
          ></DataGrid>
        </Grid>
      )}

      {productHistoryResponse?.productRecords?.length === 0 && !isSearching && (
        <div style={{ marginTop: "20px", color: "#666" }}>No history found for SKU: {productHistoryResponse.sku}</div>
      )}
    </>
  );
};

export default ProductHistory;
